import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAllUsers,
  fetchUserDetail,
  fetchAllPayments,
  fetchAllInvoices,
  fetchAdminOverview,
  extendUserPlan,
  resetUserUsage,
  resolvePaymentAdmin,
  fetchAuditLogs,
  toggleUserStatus,
  changeUserPlan,
  makeUserAdmin,
  revokeUserAdmin,
  fetchContactEnquiries,
  fetchSuspendedAccounts,
  permanentlyDeleteUser,
  reactivateUser,
  fetchUserActivity,
} from "./thunks";

const initialState = {
  users: [],
  userDetail: null,
  payments: [],
  invoices: [],
  overview: null,
  auditLogs: [],
  contactEnquiries: [],
  suspendedAccounts: [],
  userActivity: null,
  userActivityLoading: false,
  loading: false,
  error: null,
};

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    clearUserDetail: (state) => {
      state.userDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all users
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = Array.isArray(action.payload) ? action.payload : action.payload.users || [];
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch user detail
      .addCase(fetchUserDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserDetail.fulfilled, (state, action) => {
        state.loading = false;
        // API returns { user: {...} } — extract the nested user object
        state.userDetail = action.payload.user || action.payload;
      })
      .addCase(fetchUserDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch all payments
      .addCase(fetchAllPayments.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = Array.isArray(action.payload)
          ? action.payload
          : action.payload.content || action.payload.payments || [];
      })
      .addCase(fetchAllPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch all invoices
      .addCase(fetchAllInvoices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = Array.isArray(action.payload) ? action.payload : action.payload.invoices || [];
      })
      .addCase(fetchAllInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch admin overview
      .addCase(fetchAdminOverview.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdminOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.overview = action.payload;
      })
      .addCase(fetchAdminOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Extend plan — after dispatching, UserDetail re-fetches; just flag loading done
      .addCase(extendUserPlan.fulfilled, (state, action) => {
        // update planExpiryDate from response.data if available
        if (state.userDetail && action.payload?.data?.planExpiryDate) {
          state.userDetail = { ...state.userDetail, planExpiryDate: action.payload.data.planExpiryDate };
        }
      })
      // Reset usage — after dispatching, UserDetail re-fetches
      .addCase(resetUserUsage.fulfilled, (state, action) => {
        // response may contain updated usage fields
        if (state.userDetail && action.payload?.success) {
          state.userDetail = {
            ...state.userDetail,
            monthlyCustomerCount: 0,
            monthlyReminderCount: 0,
            monthlyEmailsUsed: 0,
            monthlyLeadsCreated: 0,
          };
        }
      })
      // Resolve payment
      .addCase(resolvePaymentAdmin.fulfilled, (state, action) => {
        const index = state.payments.findIndex(p => p.id === action.payload.id);
        if (index > -1) {
          state.payments[index] = action.payload;
        }
      })
      // Fetch audit logs
      .addCase(fetchAuditLogs.fulfilled, (state, action) => {
        state.auditLogs = action.payload.data || action.payload;
      })
      // Toggle user status
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        const updated = action.payload.user || action.payload;
        if (state.userDetail) {
          state.userDetail = { ...state.userDetail, ...updated };
        }
        const idx = state.users.findIndex(u => u.id === updated.id);
        if (idx > -1) state.users[idx] = { ...state.users[idx], ...updated };
      })
      // Change user plan
      .addCase(changeUserPlan.fulfilled, (state, action) => {
        const updated = action.payload.user || action.payload;
        if (state.userDetail) {
          state.userDetail = { ...state.userDetail, ...updated };
        }
        const idx = state.users.findIndex(u => u.id === updated.id);
        if (idx > -1) state.users[idx] = { ...state.users[idx], ...updated };
      })
      // Make user admin
      .addCase(makeUserAdmin.fulfilled, (state, action) => {
        if (state.userDetail) {
          state.userDetail = { ...state.userDetail, isAdmin: true };
        }
      })
      // Revoke user admin
      .addCase(revokeUserAdmin.fulfilled, (state, action) => {
        if (state.userDetail) {
          state.userDetail = { ...state.userDetail, isAdmin: false };
        }
      })
      // Fetch contact enquiries
      .addCase(fetchContactEnquiries.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchContactEnquiries.fulfilled, (state, action) => {
        state.loading = false;
        // Backend returns { tickets: [...] } or direct array
        if (Array.isArray(action.payload)) {
          state.contactEnquiries = action.payload;
        } else if (action.payload && action.payload.tickets) {
          state.contactEnquiries = action.payload.tickets;
        } else {
          state.contactEnquiries = [];
        }
      })
      .addCase(fetchContactEnquiries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch suspended accounts
      .addCase(fetchSuspendedAccounts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSuspendedAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.suspendedAccounts = action.payload?.accounts || [];
      })
      .addCase(fetchSuspendedAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Permanently delete user — remove from suspended list
      .addCase(permanentlyDeleteUser.fulfilled, (state, action) => {
        const deletedId = action.payload?.deletedUserId;
        if (deletedId) {
          state.suspendedAccounts = state.suspendedAccounts.filter(a => a.id !== deletedId);
          state.users = state.users.filter(u => u.id !== deletedId);
        }
      })
      // Reactivate user — remove from suspended list
      .addCase(reactivateUser.fulfilled, (state, action) => {
        const reactivatedUser = action.payload?.user;
        if (reactivatedUser) {
          state.suspendedAccounts = state.suspendedAccounts.filter(a => a.id !== reactivatedUser.id);
          const idx = state.users.findIndex(u => u.id === reactivatedUser.id);
          if (idx > -1) state.users[idx] = { ...state.users[idx], ...reactivatedUser };
        }
      })
      // Fetch user activity summary
      .addCase(fetchUserActivity.pending, (state) => {
        state.userActivityLoading = true;
      })
      .addCase(fetchUserActivity.fulfilled, (state, action) => {
        state.userActivityLoading = false;
        state.userActivity = action.payload;
      })
      .addCase(fetchUserActivity.rejected, (state) => {
        state.userActivityLoading = false;
        state.userActivity = null;
      });
  },
});

export const { clearUserDetail } = adminSlice.actions;
export default adminSlice.reducer;
